// src/pages/interactive-floor-plans/index.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import NavigationBar from "../../components/ui/NavigationBar";
import BreadcrumbTrail from "../../components/ui/BreadcrumbTrail";
import Icon from "../../components/AppIcon";

import FloorPlanCanvas from "./components/FloorPlanCanvas";
import LotDetailsPanel from "./components/LotDetailsPanel";
import FilterPanel from "./components/FilterPanel";
import MinimapOverview from "./components/MinimapOverview";
import ContactModal from "./components/ContactModal";
import AvailabilityStats from "./components/AvailabilityStats";
import Button from "../../components/ui/Button";

import { supabase } from "../../lib/supabase";

// =====================================================
// ✅ CONFIG
// =====================================================
const PROJECT_NAME = "Residencial 3 Volcanes";
const LOTS_TABLE = "floorplan_lots";
const PLANS_TABLE = "floor_plans";

const InteractiveFloorPlans = () => {
  const [selectedLot, setSelectedLot] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [currentView, setCurrentView] = useState("section-a");
  const [showFilters, setShowFilters] = useState(false);
  const [showMinimap, setShowMinimap] = useState(false);

  const [filters, setFilters] = useState({
    lotNumber: "",
    status: "all",
    priceRange: "all",
    areaRange: "all",
    orientation: "all",
    showUtilities: true,
    showCommonAreas: true,
    showRoads: true,
    resultsCount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [availabilityStats, setAvailabilityStats] = useState({
    total: 0,
    available: 0,
    reserved: 0,
    sold: 0,
  });

  const [adminPlans, setAdminPlans] = useState([]);
  const [activeSource, setActiveSource] = useState("dynamic"); // "dynamic" | "admin"
  const [activeAdminPlan, setActiveAdminPlan] = useState(null);

  // ✅ Por ahora: cualquiera puede editar (luego lo restringimos)
  const canEditStatus = true;

  // ✅ Responsive breakpoint (simple & reliable)
  const [isLgUp, setIsLgUp] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsLgUp(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // ✅ Plano dinámico base (image)
  const DYNAMIC_PLAN = useMemo(
    () => ({
      type: "image",
      imageUrl: "/assets/images/residencial3v_base-1770567671604.png",
      imageAlt: "Plano arquitectónico profesional - Residencial 3 Volcanes",
      sections: [],
      lots: [], // (no se usa para stats reales; stats vienen de BD)
    }),
    []
  );

  const FALLBACK_IMAGE =
    "https://img.rocket.new/generatedImages/rocket_gen_img_1c3669d0b-1768086805323.png";

  // ✅ En desktop, si resize a lg, cerramos drawers
  useEffect(() => {
    if (isLgUp) {
      setShowFilters(false);
      setShowMinimap(false);
    }
  }, [isLgUp]);

  // =====================================================
  // ✅ Cargar lista de planes admin
  // =====================================================
  useEffect(() => {
    let cancelled = false;

    const safeArray = (v) => (Array.isArray(v) ? v : []);

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: planErr } = await supabase
          .from(PLANS_TABLE)
          .select(
            `
            id,
            project_name,
            sections,
            lots,
            pdf_url,
            preview_image_url,
            created_at,
            is_active
          `
          )
          .eq("project_name", PROJECT_NAME)
          .order("created_at", { ascending: false })
          .limit(10);

        if (planErr) throw planErr;

        const plans = safeArray(data);

        if (!cancelled) {
          setAdminPlans(plans);
          const firstActive = plans.find((p) => p?.is_active) || null;
          setActiveAdminPlan(firstActive);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e?.message || String(e));
          setAdminPlans([]);
          setActiveAdminPlan(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const breadcrumbItems = [
    { label: "Inicio", path: "/landing-page" },
    { label: "Planos Interactivos", path: "/interactive-floor-plans" },
  ];

  const computedAdminPlan = activeAdminPlan
    ? {
        type: "image",
        imageUrl: activeAdminPlan?.preview_image_url || FALLBACK_IMAGE,
        imageAlt: `Plano Admin - ${activeAdminPlan?.project_name || PROJECT_NAME}`,
        sections: Array.isArray(activeAdminPlan?.sections) ? activeAdminPlan.sections : [],
        lots: Array.isArray(activeAdminPlan?.lots) ? activeAdminPlan.lots : [],
      }
    : null;

  const displayedPlan =
    activeSource === "admin" && computedAdminPlan ? computedAdminPlan : DYNAMIC_PLAN;

  // =====================================================
  // ✅ Stats reales desde BD + Realtime (auto-update)
  // =====================================================
  const loadStats = useCallback(async () => {
    try {
      const { data, error: lotsErr } = await supabase
        .from(LOTS_TABLE)
        .select("status")
        .eq("project_name", PROJECT_NAME);

      if (lotsErr) throw lotsErr;

      const rows = Array.isArray(data) ? data : [];
      const total = rows.length;
      const available = rows.filter((l) => l?.status === "available").length;
      const reserved = rows.filter((l) => l?.status === "reserved").length;
      const sold = rows.filter((l) => l?.status === "sold").length;

      setAvailabilityStats({ total, available, reserved, sold });
      setFilters((p) => ({ ...p, resultsCount: total }));
    } catch (e) {
      console.error("Error loading availability stats:", e);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (cancelled) return;
      await loadStats();
    };
    boot();

    const channel = supabase
      .channel("floorplan-lots-stats")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: LOTS_TABLE,
          filter: `project_name=eq.${PROJECT_NAME}`,
        },
        () => {
          loadStats();
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [loadStats]);

  // =====================================================
  // ✅ Update lote (status + customer_name + deal_amount + area_m2 + price_total + discount_enabled)
  // =====================================================
  const handleUpdateLot = useCallback(
    async ({ id, status, customer_name, deal_amount, area_m2, price_total, discount_enabled }) => {
      // Por ahora: cualquiera puede editar
      // Luego: valida auth aquí

      const { error: upErr } = await supabase
        .from(LOTS_TABLE)
        .update({
          status,
          customer_name: customer_name ?? null,
          deal_amount: deal_amount ?? null,
          area_m2: area_m2 ?? null,
          price_total: price_total ?? null,
          discount_enabled: !!discount_enabled,
        })
        .eq("project_name", PROJECT_NAME)
        .eq("lot_code", id);

      if (upErr) throw upErr;

      // ✅ refresca panel al toque
      setSelectedLot((prev) => {
        if (!prev) return prev;
        const prevId = prev?.id ?? prev?.lot_code ?? prev?.number;
        if (String(prevId) !== String(id)) return prev;

        return {
          ...prev,
          status,
          customer_name: customer_name ?? null,
          deal_amount: deal_amount ?? null,
          area_m2: area_m2 ?? null,
          price_total: price_total ?? null,
          discount_enabled: !!discount_enabled,
        };
      });

      // stats se actualizarán por realtime, pero si quieres inmediato:
      // await loadStats();
    },
    [loadStats]
  );

  const handleNavigateToSection = (section) => setCurrentView(section?.id);

  const handleContactSubmit = (formData) => {
    console.log("Contact form submitted:", formData);
    setShowContactModal(false);
    setSelectedLot(null);
  };

  const handleResetFilters = () => {
    setFilters((prev) => ({
      ...prev,
      lotNumber: "",
      status: "all",
      priceRange: "all",
      areaRange: "all",
      orientation: "all",
      showUtilities: true,
      showCommonAreas: true,
      showRoads: true,
      resultsCount: availabilityStats?.total || 0,
    }));
  };

  // ✅ Helpers UI: overlays mobile/tablet
  const closeAllOverlays = () => {
    setShowFilters(false);
    setShowMinimap(false);
  };

  const openFilters = () => {
    setShowFilters(true);
    if (!isLgUp) setShowMinimap(false);
  };

  const openMinimap = () => {
    setShowMinimap(true);
    if (!isLgUp) setShowFilters(false);
  };

  // ✅ Canvas height: usable on mobile
  const canvasHeightClass = useMemo(() => {
    if (isLgUp) return "h-[72vh] min-h-[520px]";
    return "h-[60vh] min-h-[420px]";
  }, [isLgUp]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando planos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Planos Interactivos - {PROJECT_NAME} | Explora Lotes Disponibles</title>
        <meta
          name="description"
          content={`Explora nuestros planos interactivos y encuentra el lote perfecto en ${PROJECT_NAME}.`}
        />
        <meta property="og:title" content={`Planos Interactivos - ${PROJECT_NAME}`} />
        <meta property="og:description" content="Explora lotes disponibles con nuestro sistema interactivo de planos" />
        <meta property="og:image" content={computedAdminPlan?.imageUrl || FALLBACK_IMAGE} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
          <BreadcrumbTrail items={breadcrumbItems} />

          {error && (
            <div className="mb-4 p-4 rounded-xl bg-destructive/10 border border-destructive/30">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">Ocurrió un error cargando datos reales</p>
                  <p className="text-xs text-destructive/80 break-words">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  Planos Interactivos
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                  Explora y selecciona tu lote ideal con nuestra herramienta interactiva
                </p>
              </div>

              {/* Actions (Desktop / Tablet) */}
              <div className="hidden sm:flex flex-wrap items-center gap-2 sm:gap-3">
                <Button
                  variant={activeSource === "dynamic" ? "default" : "outline"}
                  iconName="Layers"
                  onClick={() => setActiveSource("dynamic")}
                  className="text-xs sm:text-sm"
                >
                  Plano Dinámico
                </Button>

                <Button
                  variant={activeSource === "admin" ? "default" : "outline"}
                  iconName="File"
                  onClick={() => setActiveSource("admin")}
                  disabled={!activeAdminPlan}
                  className="text-xs sm:text-sm"
                >
                  Planos Admin {activeAdminPlan ? "" : "(no hay)"}
                </Button>

                <Button
                  variant="outline"
                  iconName="Filter"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-xs sm:text-sm"
                >
                  {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                </Button>

                <Button
                  variant="outline"
                  iconName="Map"
                  onClick={() => setShowMinimap(!showMinimap)}
                  className="text-xs sm:text-sm"
                >
                  {showMinimap ? "Ocultar Minimapa" : "Mostrar Minimapa"}
                </Button>

                <Button
                  variant="default"
                  iconName="Phone"
                  onClick={() => setShowContactModal(true)}
                  className="text-xs sm:text-sm"
                >
                  Contactar Asesor
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-4 sm:mb-6">
            <AvailabilityStats stats={availabilityStats} />
          </div>

          {/* Main (Desktop grid) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            {/* Filtros Desktop (sidebar) */}
            {isLgUp && showFilters && (
              <div className="lg:col-span-3">
                <div className="card p-4 sm:p-5 sticky top-24">
                  <FilterPanel
                    filters={filters}
                    onFilterChange={setFilters}
                    onResetFilters={handleResetFilters}
                    resultsCount={filters?.resultsCount}
                  />
                </div>
              </div>
            )}

            {/* Canvas */}
            <div className={isLgUp ? (showFilters ? "lg:col-span-6" : "lg:col-span-9") : "lg:col-span-9"}>
              <div className="card p-2 sm:p-4 lg:p-6">
                <div className={`${canvasHeightClass} w-full`}>
                  <FloorPlanCanvas
                    floorPlanData={displayedPlan}
                    selectedLot={selectedLot}
                    onLotSelect={setSelectedLot}
                    currentView={currentView}
                    onViewChange={setCurrentView}
                    filters={filters}
                  />
                </div>
              </div>
            </div>

            {/* Detalle Desktop (sidebar) */}
            {isLgUp && (
              <div className="lg:col-span-3">
                <div className="card p-4 sm:p-5 sticky top-24">
                  {selectedLot ? (
                    <LotDetailsPanel
                      lot={selectedLot}
                      onContactInterest={() => setShowContactModal(true)}
                      onClose={() => setSelectedLot(null)}
                      canEditStatus={canEditStatus}
                      onUpdateLot={handleUpdateLot}
                    />
                  ) : (
                    <div className="text-center py-8 sm:py-12">
                      <Icon name="MousePointer" size={40} className="mx-auto mb-3 sm:mb-4 text-muted-foreground" />
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Selecciona un lote en el plano para ver sus detalles
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Minimap Desktop */}
          {isLgUp && showMinimap && (
            <div className="fixed bottom-4 right-4 z-40 w-56 sm:w-72">
              <MinimapOverview
                floorPlanData={displayedPlan}
                currentView={currentView}
                onNavigate={handleNavigateToSection}
                onClose={() => setShowMinimap(false)}
              />
            </div>
          )}
        </div>

        {/* ============================
            ✅ Mobile / Tablet UX Layers
           ============================ */}

        {/* Bottom Action Bar (solo móvil) */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur">
          <div className="container mx-auto px-3 py-2 flex items-center justify-between gap-2">
            <button
              className={`flex-1 h-10 rounded-xl border text-xs font-semibold ${
                activeSource === "dynamic" ? "bg-primary text-white border-primary" : "bg-card"
              }`}
              onClick={() => setActiveSource("dynamic")}
            >
              Dinámico
            </button>

            <button
              className={`flex-1 h-10 rounded-xl border text-xs font-semibold ${
                activeSource === "admin" ? "bg-primary text-white border-primary" : "bg-card"
              } ${!activeAdminPlan ? "opacity-50" : ""}`}
              onClick={() => activeAdminPlan && setActiveSource("admin")}
              disabled={!activeAdminPlan}
            >
              Admin
            </button>

            <button
              className="w-12 h-10 rounded-xl border bg-card flex items-center justify-center"
              onClick={openFilters}
              title="Filtros"
            >
              <Icon name="Filter" size={18} />
            </button>

            <button
              className="w-12 h-10 rounded-xl border bg-card flex items-center justify-center"
              onClick={openMinimap}
              title="Minimapa"
            >
              <Icon name="Map" size={18} />
            </button>

            <button
              className="w-12 h-10 rounded-xl bg-primary text-white flex items-center justify-center"
              onClick={() => setShowContactModal(true)}
              title="Contactar"
            >
              <Icon name="Phone" size={18} />
            </button>
          </div>
        </div>

        {/* Overlay fondo (mobile/tablet) */}
        {!isLgUp && (showFilters || showMinimap || selectedLot) && (
          <button
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => {
              closeAllOverlays();
            }}
            aria-label="Cerrar overlay"
          />
        )}

        {/* Drawer filtros (mobile/tablet) */}
        {!isLgUp && showFilters && (
          <div className="fixed z-50 left-0 right-0 bottom-0 max-h-[85vh] rounded-t-2xl bg-background border-t shadow-2xl">
            <div className="px-4 py-3 flex items-center justify-between border-b">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={18} />
                <p className="font-semibold text-sm">Filtros</p>
              </div>
              <button
                className="w-9 h-9 rounded-xl border bg-card flex items-center justify-center"
                onClick={() => setShowFilters(false)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[72vh]">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onResetFilters={handleResetFilters}
                resultsCount={filters?.resultsCount}
              />
            </div>
          </div>
        )}

        {/* Drawer minimapa (mobile/tablet) */}
        {!isLgUp && showMinimap && (
          <div className="fixed z-50 left-0 right-0 bottom-0 max-h-[70vh] rounded-t-2xl bg-background border-t shadow-2xl">
            <div className="px-4 py-3 flex items-center justify-between border-b">
              <div className="flex items-center gap-2">
                <Icon name="Map" size={18} />
                <p className="font-semibold text-sm">Minimapa</p>
              </div>
              <button
                className="w-9 h-9 rounded-xl border bg-card flex items-center justify-center"
                onClick={() => setShowMinimap(false)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-3">
              <MinimapOverview
                floorPlanData={displayedPlan}
                currentView={currentView}
                onNavigate={(section) => {
                  handleNavigateToSection(section);
                  setShowMinimap(false);
                }}
                onClose={() => setShowMinimap(false)}
              />
            </div>
          </div>
        )}

        {/* Bottom Sheet Detalle lote (mobile/tablet) */}
        {!isLgUp && selectedLot && (
          <div className="fixed z-50 left-0 right-0 bottom-0 max-h-[85vh] rounded-t-2xl bg-background border-t shadow-2xl">
            <div className="px-4 py-3 flex items-center justify-between border-b">
              <div className="flex items-center gap-2">
                <Icon name="Home" size={18} />
                <p className="font-semibold text-sm">Detalle del Lote</p>
              </div>
              <button
                className="w-9 h-9 rounded-xl border bg-card flex items-center justify-center"
                onClick={() => setSelectedLot(null)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[72vh]">
              <LotDetailsPanel
                lot={selectedLot}
                onContactInterest={() => setShowContactModal(true)}
                onClose={() => setSelectedLot(null)}
                canEditStatus={canEditStatus}
                onUpdateLot={handleUpdateLot}
              />
            </div>
          </div>
        )}

        {/* Contact Modal */}
        {showContactModal && (
          <ContactModal lot={selectedLot} onClose={() => setShowContactModal(false)} onSubmit={handleContactSubmit} />
        )}

        {/* Spacer para no tapar contenido en móvil por la bottom bar */}
        <div className="sm:hidden h-16" />
      </div>
    </>
  );
};

export default InteractiveFloorPlans;
